import React from "react";
import { ImageUploader } from "antd-mobile";
import PreviewImages from "../PreviewImages";
import VideoPlayer from "../../pages/vehicle/report/components/MediaList/VideoPlayer";
import MessageBox from "../message-box";
import {
  ATTACHMENT_MAX,
  ATTACHMENT_MAX_SIZE,
  IMAGE_MAX_SIZE,
  ALLOWED_IMAGE_EXT,
  ALLOWED_VIDEO_EXT,
} from "./service";
import { useAttachmentUpload, UploadItem } from "./useAttachmentUpload";
import styles from "./index.module.scss";

interface AttachmentUploadProps {
  /** 已选附件的 fileId 列表（回显/受控） */
  value?: string[];
  /** 变更时回调，抛出最新 fileId 列表 */
  onChange?: (fileIds: string[]) => void;
  /** 字段标题，默认「上传图片/视频」 */
  title?: string;
  /** 最大数量，默认 9 */
  maxCount?: number;
  /** 单个附件最大字节数，默认 50M */
  maxSize?: number;
  /** 图片单独最大字节数，默认 20M */
  imageMaxSize?: number;
  /** 允许的扩展名白名单（小写、不含点），默认 jpg/jpeg/png/mp4/mov */
  allowedExts?: string[];
  /** 文件选择 accept，默认 image/*,video/* */
  accept?: string;
  /** 只读展示：隐藏上传按钮与删除键，仅缩略图 + 点击预览/播放 */
  readOnly?: boolean;
}

/**
 * 附件上传（网格模式）：上传格 + 缩略图一体的 ImageUploader 网格。
 * 上传/校验/回显/预览/视频封面逻辑复用 useAttachmentUpload hook。
 */
export default function AttachmentUpload(props: AttachmentUploadProps) {
  const {
    value,
    onChange,
    title = "上传图片/视频",
    maxCount = ATTACHMENT_MAX,
    maxSize = ATTACHMENT_MAX_SIZE,
    imageMaxSize = IMAGE_MAX_SIZE,
    allowedExts = [...ALLOWED_IMAGE_EXT, ...ALLOWED_VIDEO_EXT],
    accept = "image/*,video/*",
    readOnly = false,
  } = props;

  const {
    items,
    emit,
    beforeUpload,
    doUpload,
    handlePreview,
    renderItem,
    previewRef,
    videoUrl,
    videoVisible,
    setVideoVisible,
  } = useAttachmentUpload({
    value,
    onChange,
    maxCount,
    maxSize,
    imageMaxSize,
    allowedExts,
  });

  return (
    <div className={styles.attachmentField}>
      <ImageUploader
        value={items}
        onChange={(next) => emit(next as UploadItem[])}
        upload={doUpload}
        beforeUpload={beforeUpload}
        showFailed={false}
        multiple
        maxCount={maxCount}
        onCountExceed={(exceed) =>
          MessageBox.toast(`最多上传 ${maxCount} 个，超出的 ${exceed} 个未添加`)
        }
        accept={accept}
        preview={false}
        onPreview={handlePreview}
        showUpload={!readOnly}
        deletable={!readOnly}
        renderItem={renderItem}
      >
        {/* 自定义上传格：+ 号下方显示文案（在格子内部） */}
        <div className={styles.uploadButton}>
          <span className={styles.plusIcon} />
          <span className={styles.uploadText}>{title}</span>
        </div>
      </ImageUploader>

      <PreviewImages ref={previewRef} />
      {videoVisible && (
        <VideoPlayer src={videoUrl} onClose={() => setVideoVisible(false)} />
      )}
    </div>
  );
}
